import React, { useMemo, useState } from 'react';
import { App as AntdApp, Card, DatePicker, Checkbox, Form, Button, Empty, Spin, Tag, InputNumber } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { buildApiUrl } from '../config/api';
import '../styles/Common.css';

const TYPE_OPTIONS = ['화물택배', '납품시공', 'AS', '수출'];
const LABELS_PER_PAGE = 10;
const EXCLUDED_COLUMN_KEYS = new Set(['region', 'status', 'process', 'times', 'sashCode', 'totalQty', 'leftQty', 'rightQty']);
const DEFAULT_COLUMNS = [
  { key: 'shipDate', label: '출고일' },
  { key: 'delivery', label: '배송' },
  { key: 'productName', label: '제 품 명' },
  { key: 'fabricCode', label: '원단코드' },
  { key: 'width', label: '가로' },
  { key: 'height', label: '세로' },
  { key: 'direction', label: '방향' },
  { key: 'totalQty', label: '총수량' },
  { key: 'customerName', label: '거래처명' },
  { key: 'note', label: '비고' },
];

const BmsDataPage = () => {
  const { message } = AntdApp.useApp();
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resultColumns, setResultColumns] = useState(DEFAULT_COLUMNS);
  const [resultRows, setResultRows] = useState([]);
  const [autoCalcEdits, setAutoCalcEdits] = useState({});

  const isAllChecked = useMemo(
    () => TYPE_OPTIONS.every((type) => selectedTypes.includes(type)),
    [selectedTypes]
  );

  const handleAllChange = (e) => {
    if (e.target.checked) {
      setSelectedTypes(TYPE_OPTIONS);
      return;
    }
    setSelectedTypes([]);
  };

  const handleSearch = async () => {
    if (!startDate || !endDate) {
      message.warning('시작일과 종료일을 선택해 주세요.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(buildApiUrl('/api/bms/roman-shade/search'), {
        startDate: startDate.format('YYYY/MM/DD'),
        endDate: endDate.format('YYYY/MM/DD'),
        selectedTypes,
      });

      const sourceColumns = response.data?.columns || DEFAULT_COLUMNS;
      const visibleColumns = sourceColumns.filter((col) => !EXCLUDED_COLUMN_KEYS.has(col.key));
      const withDirectionColumn = visibleColumns.some((col) => col.key === 'direction')
        ? visibleColumns
        : [
            ...visibleColumns,
            { key: 'direction', label: '방향' },
          ];
      const structuredRows = response.data?.structuredRows;
      const legacyRows = response.data?.rows || [];

      setResultColumns(withDirectionColumn);
      setAutoCalcEdits({});
      if (Array.isArray(structuredRows)) {
        setResultRows(structuredRows);
        message.success(`조회 완료: ${structuredRows.length}건`);
      } else {
        const mappedRows = legacyRows.map((row) => {
          const obj = {};
          sourceColumns.forEach((col, index) => {
            obj[col.key] = row[index] || '';
          });
          return obj;
        });
        setResultRows(mappedRows);
        message.success(`조회 완료: ${mappedRows.length}건`);
      }
    } catch (error) {
      console.error('[BMS 데이터 조회 실패]', {
        message: error?.message,
        responseStatus: error?.response?.status,
        responseData: error?.response?.data,
        error,
      });
      const errorMessage = error.response?.data?.message || error.message;
      message.error(errorMessage);
      setResultColumns(DEFAULT_COLUMNS);
      setResultRows([]);
      setAutoCalcEdits({});
    } finally {
      setLoading(false);
    }
  };

  const cardFieldColumns = useMemo(() => {
    const baseFields = resultColumns.filter((col) => !['shipDate', 'delivery', 'productName'].includes(col.key));
    const preferredOrder = ['width', 'height', 'direction', 'note'];

    const preferredFields = preferredOrder
      .map((key) => baseFields.find((col) => col.key === key))
      .filter(Boolean);

    const remainingFields = baseFields.filter((col) => !preferredOrder.includes(col.key));

    return [...remainingFields, ...preferredFields];
  }, [resultColumns]);

  const cardRows = useMemo(() => {
    return resultRows.flatMap((row, rowIndex) => {
      const linkedWindows = Array.isArray(row.linkedWindows) ? row.linkedWindows : [];
      if (linkedWindows.length === 0) {
        return [{ ...row, _cardKey: `row-${rowIndex}-single`, linkedLabel: '' }];
      }

      return linkedWindows.map((windowItem, panelIndex) => ({
        ...row,
        width: windowItem.width || row.width,
        height: windowItem.height || row.height,
        direction: windowItem.direction || row.direction || '-',
        linkedLabel: linkedWindows.length > 1
          ? (windowItem.label || `${linkedWindows.length}-${panelIndex + 1}`)
          : '',
        _cardKey: `row-${rowIndex}-panel-${panelIndex + 1}`,
      }));
    });
  }, [resultRows]);

  const toNumber = (value) => {
    if (value === null || value === undefined) {
      return 0;
    }
    const normalized = String(value).replace(/,/g, '').trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const totalWindowCount = useMemo(() => (
    cardRows.filter((row) => toNumber(row.height) > 1).length
  ), [cardRows]);

  const toMmText = (value) => {
    if (!Number.isFinite(value) || value <= 0) {
      return '-';
    }
    if (Number.isInteger(value)) {
      return `${value} mm`;
    }
    return `${value.toFixed(1)} mm`;
  };

  const toInt = (value) => Math.max(0, Math.round(toNumber(value)));

  const buildAutoCalc = (row) => {
    const width = toNumber(row.width);
    const height = toNumber(row.height);

    const cutWidth = toInt(width);
    const cutHeight = toInt(height);

    const lineCount = 8;
    const lineGap = height > 0 ? toInt(height / (lineCount + 1)) : 0;
    const lines = Array.from({ length: lineCount }, (_, idx) => toInt(lineGap * (idx + 1)));
    const pocketSize = 25;

    return {
      cutWidth,
      cutHeight,
      lines,
      pocketSize,
    };
  };

  const getCardAutoCalc = (cardKey, baseCalc) => {
    const edit = autoCalcEdits[cardKey];
    if (!edit) {
      return baseCalc;
    }

    return {
      cutWidth: toInt(edit.cutWidth ?? baseCalc.cutWidth),
      cutHeight: toInt(edit.cutHeight ?? baseCalc.cutHeight),
      pocketSize: toInt(edit.pocketSize ?? baseCalc.pocketSize),
      lines: Array.isArray(edit.lines) ? edit.lines.map((line) => toInt(line)) : baseCalc.lines,
    };
  };

  const updateCardAutoCalc = (cardKey, partial) => {
    setAutoCalcEdits((prev) => ({
      ...prev,
      [cardKey]: {
        ...(prev[cardKey] || {}),
        ...partial,
      },
    }));
  };

  const printLabelPages = useMemo(() => {
    const labels = cardRows.map((row, index) => {
      const cardKey = row._cardKey || `${row.shipDate || 'no-date'}-${index}`;
      const autoCalc = getCardAutoCalc(cardKey, buildAutoCalc(row));

      return {
        key: cardKey,
        customerName: row.customerName || '-',
        shipDate: row.shipDate || '-',
        delivery: row.delivery || '-',
        productName: row.productName || '-',
        direction: row.direction || '-',
        linkedLabel: row.linkedLabel || '',
        width: row.width || '-',
        height: row.height || '-',
        cutWidth: toMmText(autoCalc.cutWidth),
        cutHeight: toMmText(autoCalc.cutHeight),
        pocketSize: toMmText(autoCalc.pocketSize),
        linesText: autoCalc.lines.map((lineValue, lineIndex) => `L${lineIndex + 1}:${toMmText(lineValue)}`).join(' / '),
        note: row.note || '-',
      };
    });

    const pages = [];
    for (let i = 0; i < labels.length; i += LABELS_PER_PAGE) {
      pages.push(labels.slice(i, i + LABELS_PER_PAGE));
    }

    return pages;
  }, [cardRows, autoCalcEdits]);

  return (
    <>
      <style>
        {`@media print {
          @page {
            size: A4 portrait !important;
            margin: 0 !important;
          }

          html,
          body {
            width: 210mm !important;
            height: 297mm !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }

          .bms-label-print-area.print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            padding: 0 !important;
            margin: 0 !important;
            display: block !important;
            background: #ffffff !important;
            transform: none !important;
            overflow: hidden !important;
          }

          .bms-label-page {
            width: 210mm;
            height: 297mm;
            display: grid;
            grid-template-columns: repeat(2, 90mm);
            grid-template-rows: repeat(5, 50mm);
            justify-content: start;
            align-content: start;
            column-gap: 0;
            row-gap: 0;
            padding: 23.5mm 15mm;
            box-sizing: border-box;
            page-break-after: always;
            break-after: page;
          }

          .bms-label-page:last-child {
            page-break-after: auto;
            break-after: auto;
          }

          .bms-label-item {
            border: 0.25mm solid #222;
            border-radius: 0;
            padding: 2mm;
            box-sizing: border-box;
            display: grid;
            gap: 0.8mm;
            align-content: start;
            font-size: 8.5px;
            overflow: hidden;
          }

          .bms-label-item-empty {
            border: 1px dashed #d1d5db;
            border-radius: 2mm;
          }

          .bms-label-title {
            font-size: 9.5px;
            font-weight: 700;
          }

          .bms-label-line {
            display: flex;
            gap: 2mm;
            flex-wrap: wrap;
          }

          .bms-label-note {
            font-size: 8px;
            line-height: 1.2;
            word-break: break-word;
          }
        }`}
      </style>

      <Card size="small" styles={{ body: { padding: 12 } }} style={{ marginBottom: 8 }}>
        <Form
          layout="inline"
          style={{
            rowGap: 8,
            columnGap: 8,
          }}
        >
          <Form.Item label="시작일" style={{ marginBottom: 0 }}>
            <DatePicker
              value={startDate}
              onChange={setStartDate}
              style={{ width: 136 }}
              format="YYYY-MM-DD"
            />
          </Form.Item>

          <Form.Item label="종료일" style={{ marginBottom: 0 }}>
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              style={{ width: 136 }}
              format="YYYY-MM-DD"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Checkbox checked={isAllChecked} onChange={handleAllChange}>
              전체
            </Checkbox>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Checkbox.Group
              options={TYPE_OPTIONS}
              value={selectedTypes}
              onChange={setSelectedTypes}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginLeft: 'auto' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={() => window.print()} disabled={loading || cardRows.length === 0}>
                출력
              </Button>
              <Button type="primary" onClick={handleSearch} loading={loading} disabled={loading}>
                검색
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>

      <Card size="small" styles={{ body: { padding: 10 } }} style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tag color="geekblue" style={{ margin: 0, padding: '4px 8px' }}>
            총 제작창 {totalWindowCount}창
          </Tag>
        </div>
      </Card>

      <Card
        className="bms-list-shell"
        size="small"
        style={{ backgroundColor: '#f0f2f5' }}
        styles={{ body: { padding: 8, backgroundColor: '#f0f2f5' } }}
      >
        <Spin spinning={loading}>
          {cardRows.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="조회 결과가 없습니다." />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
              {cardRows.map((row, index) => (
                (() => {
                  const cardKey = row._cardKey || `${row.shipDate || 'no-date'}-${index}`;
                  const autoCalc = getCardAutoCalc(cardKey, buildAutoCalc(row));
                  const shouldShowAutoCalc = toNumber(row.height) > 1;
                  const isAccessoryCard = toNumber(row.width) === 1 || toNumber(row.height) === 1;
                  return (
                    <Card
                      className={`bms-item-card${isAccessoryCard ? ' is-accessory' : ''}`}
                      key={cardKey}
                      size="small"
                      title={row.customerName || '-'}
                      extra={(
                        <div style={{ display: 'flex', gap: 6 }}>
                          {row.linkedLabel ? <Tag color="green">연창 {row.linkedLabel}</Tag> : null}
                          <Tag color="blue">{row.delivery || '-'}</Tag>
                        </div>
                      )}
                      styles={{ body: { padding: 10 } }}
                    >
                      <div style={{ display: 'grid', gap: 10 }}>
                        <div style={{ display: 'grid', gap: 6 }}>
                          <div><strong>출고일</strong>: {row.shipDate || '-'}</div>
                          <div><strong>제 품 명</strong>: {row.productName || '-'}</div>
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <span><strong>가로</strong>: {row.width || '-'}</span>
                            <span><strong>세로</strong>: {row.height || '-'}</span>
                            <span><strong>방향</strong>: {row.direction || '-'}</span>
                          </div>
                          {cardFieldColumns
                            .filter((col) => !['customerName', 'width', 'height', 'direction'].includes(col.key))
                            .map((col) => (
                              <div key={col.key}>
                                <strong>{col.label}</strong>: {row[col.key] || '-'}
                              </div>
                            ))}
                        </div>

                        {shouldShowAutoCalc && (
                          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, display: 'grid', gap: 6 }}>
                            <strong>자동 연산</strong>
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                              <span>
                                <strong>제단 폭</strong>:
                                <InputNumber
                                  size="small"
                                  min={0}
                                  step={1}
                                  precision={0}
                                  value={autoCalc.cutWidth}
                                  onChange={(value) => updateCardAutoCalc(cardKey, { cutWidth: toInt(value) })}
                                  style={{ width: 110, marginLeft: 6 }}
                                />
                              </span>
                              <span>
                                <strong>제단 높이</strong>:
                                <InputNumber
                                  size="small"
                                  min={0}
                                  step={1}
                                  precision={0}
                                  value={autoCalc.cutHeight}
                                  onChange={(value) => updateCardAutoCalc(cardKey, { cutHeight: toInt(value) })}
                                  style={{ width: 110, marginLeft: 6 }}
                                />
                              </span>
                              <span>
                                <strong>봉집 크기</strong>:
                                <InputNumber
                                  size="small"
                                  min={0}
                                  step={1}
                                  precision={0}
                                  value={autoCalc.pocketSize}
                                  onChange={(value) => updateCardAutoCalc(cardKey, { pocketSize: toInt(value) })}
                                  style={{ width: 110, marginLeft: 6 }}
                                />
                              </span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', gap: 4 }}>
                              {autoCalc.lines.map((lineValue, lineIndex) => (
                                <div key={`line-${lineIndex + 1}`}>
                                  <strong>라인 {lineIndex + 1}</strong>:
                                  <InputNumber
                                    size="small"
                                    min={0}
                                    step={1}
                                    precision={0}
                                    value={lineValue}
                                    onChange={(value) => {
                                      const nextLines = [...autoCalc.lines];
                                      nextLines[lineIndex] = toInt(value);
                                      updateCardAutoCalc(cardKey, { lines: nextLines });
                                    }}
                                    style={{ width: 110, marginLeft: 6 }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })()
              ))}
            </div>
          )}
        </Spin>
      </Card>

      <div className="print-area print-only bms-label-print-area">
        {printLabelPages.map((pageItems, pageIndex) => (
          <div key={`label-page-${pageIndex + 1}`} className="bms-label-page">
            {pageItems.map((item, itemIndex) => (
              <div key={`${item.key}-print-${itemIndex + 1}`} className="bms-label-item">
                <div className="bms-label-title">{item.customerName}</div>
                <div className="bms-label-line">
                  <span><strong>출고일</strong>: {item.shipDate}</span>
                  <span><strong>배송</strong>: {item.delivery || '-'}</span>
                  {item.linkedLabel ? <span><strong>연창</strong>: 연창 {item.linkedLabel}</span> : null}
                </div>
                <div className="bms-label-line">
                  <span><strong>가로</strong>: {item.width}</span>
                  <span><strong>세로</strong>: {item.height}</span>
                  <span><strong>방향</strong>: {item.direction}</span>
                </div>
                <div className="bms-label-note"><strong>비고</strong>: {item.note}</div>
                <div className="bms-label-line">
                  <span><strong>제단 폭</strong>: {item.cutWidth}</span>
                  <span><strong>제단 높이</strong>: {item.cutHeight}</span>
                  <span><strong>봉집 크기</strong>: {item.pocketSize}</span>
                </div>
                <div className="bms-label-note"><strong>자동연산 라인</strong>: {item.linesText}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

export default BmsDataPage;
